import {
  IconAlertCircle,
  IconArrowsLeftRight,
  IconBell,
  IconBellOff,
  IconBolt,
  IconChevronRight,
  IconCurrencyDollar,
  IconEye,
  IconHeart,
  IconNews,
  IconSpeakerphone,
  IconStack2,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import imgAygLogo from "@/assets/images/LOGOTIPO_AYG_v3.0.avif";
import useScrollLock from "@/hooks/useScrollLock";
import useNotificationsFeed from "../../hooks/notifications/useNotificationsFeed";
import usePutMarkAsReadNotification from "../../hooks/notifications/usePutNotification";
import styles from "./notification.module.css";

const TABS = [
  {key: "all", icon: IconStack2},
  {key: "news", icon: IconNews},
  {key: "breaking", icon: IconBolt},
  {key: "movements", icon: IconArrowsLeftRight},
];

const MOCK_PUSH = [
  {
    id: "push-mov-1",
    type: "push",
    subtype: "money",
    title: "Depósito acreditado: Bs. 2.500,00",
    body: "Tu depósito vía Pago Móvil fue procesado exitosamente. Saldo disponible.",
    image: null,
    link: "/user/transactions",
    createdAt: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
    isRead: false,
    originalId: "mov-1",
  },
  {
    id: "push-mov-2",
    type: "push",
    subtype: "money",
    title: "Retiro procesado: $50.00",
    body: "Tu retiro a la cuenta bancaria ***4821 está en proceso. Tiempo estimado: 24h.",
    image: null,
    link: "/user/transactions",
    createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    isRead: false,
    originalId: "mov-2",
  },
  {
    id: "push-1",
    type: "push",
    subtype: "promo",
    title: "Bonificación activa 🎁",
    body: "200% de bonificación disponible durante los próximos 30 minutos.",
    image: null,
    link: null,
    createdAt: new Date(Date.now() - 8 * 60 * 1000).toISOString(),
    isRead: false,
    originalId: "promo-1",
  },
  {
    id: "push-2",
    type: "push",
    subtype: "promo",
    title: "Bono de bienvenida activado",
    body: "Tienes un bono del 100% en tu primer depósito. Válido por 7 días.",
    image: null,
    link: null,
    createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    isRead: true,
    originalId: "promo-2",
  },
  {
    id: "push-mov-3",
    type: "push",
    subtype: "money",
    title: "Transferencia recibida: Bs. 1.000,00",
    body: "Has recibido una transferencia de usuario @carlos_89.",
    image: null,
    link: "/user/transactions",
    createdAt: new Date(Date.now() - 26 * 60 * 60 * 1000).toISOString(),
    isRead: true,
    originalId: "mov-3",
  },
];

const NEW_NOTIFICATION = {
  id: "push-new",
  type: "push",
  subtype: "money",
  title: "Depósito acreditado: $100.00",
  body: "Tu depósito con tarjeta Visa ***7723 fue acreditado exitosamente.",
  image: null,
  link: "/user/transactions",
  createdAt: new Date().toISOString(),
  isRead: false,
  originalId: "mov-new",
};

const MOCK_BANK_STATUS = [
  {code: "102", name: "Banco de Venezuela", isActive: true},
  {code: "134", name: "Banesco", isActive: true},
  {code: "105", name: "Mercantil", isActive: true},
  {code: "108", name: "Provincial", isActive: false},
  {code: "115", name: "Banco Exterior", isActive: true},
];

const formatTimeAgo = (dateString, t) => {
  const diff = Date.now() - new Date(dateString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return t("notificationJustNow");
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(dateString).toLocaleDateString();
};

const NewsCard = ({item, onNavigate, onDismiss, t}) => {
  const handleClick = () => {
    if (item.link) onNavigate(item);
  };

  const handleDismiss = e => {
    e.stopPropagation();
    onDismiss(item.id);
  };

  return (
    <div
      className={styles.newsCard}
      role="article"
      aria-label={item.title}
    >
      <div className={styles.newsBanner}>
        {item.image ? (
          <img
            src={item.image}
            alt=""
            className={styles.newsBannerImg}
            loading="lazy"
          />
        ) : (
          <div className={styles.newsBannerPlaceholder}>
            <IconNews size={32} />
          </div>
        )}
      </div>
      <div className={styles.newsBody}>
        <span className={`${styles.cardTag} ${styles.tagNews}`}>{t("notificationTagNews")}</span>
        <p className={styles.newsTitle}>{item.title}</p>
        <span className={styles.newsDate}>
          {new Date(item.createdAt).toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"})}
          {" · "}
          {new Date(item.createdAt).toLocaleTimeString(undefined, {hour: "2-digit", minute: "2-digit"})}
        </span>

        <div className={styles.newsFooter}>
          <button
            type="button"
            className={styles.ctaBtn}
            onClick={handleClick}
          >
            {t("notificationNewsCta")}
            <IconChevronRight size={14} />
          </button>
          <button
            type="button"
            className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
            onClick={handleDismiss}
            aria-label={t("notificationDrawerDeleteBtn")}
          >
            <IconTrash size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

const PushCard = ({item, onNavigate, onDelete, onMarkRead, t}) => {
  const handleClick = () => {
    if (item.link) onNavigate(item);
  };

  const handleDelete = e => {
    e.stopPropagation();
    if (item.originalId) onDelete(item.originalId);
  };

  const handleMarkRead = e => {
    e.stopPropagation();
    if (!item.isRead && item.originalId) onMarkRead(item.originalId);
  };

  return (
    <div
      className={`${styles.card} ${!item.isRead ? styles.cardUnread : ""}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === "Enter" && handleClick()}
      aria-label={item.title}
    >
      <div className={`${styles.cardIcon} ${item.subtype === "money" ? styles.cardIconMoney : ""}`}>
        {item.subtype === "money" ? <IconCurrencyDollar size={18} /> : <IconSpeakerphone size={18} />}
      </div>

      <div className={styles.cardContent}>
        <p className={styles.cardTitle}>{item.title}</p>
        {item.body && <p className={styles.cardBody}>{item.body}</p>}
        <div className={styles.cardMeta}>
          <span className={styles.cardTime}>{formatTimeAgo(item.createdAt, t)}</span>
          <span className={`${styles.cardTag} ${item.subtype === "money" ? styles.tagMoney : styles.tagBreaking}`}>
            {item.subtype === "money" ? t("notificationTagMovement") : t("notificationTagBreaking")}
          </span>
        </div>
      </div>

      <div className={styles.cardActions}>
        {!item.isRead && (
          <button
            type="button"
            className={styles.actionBtn}
            onClick={handleMarkRead}
            aria-label={t("notificationMarkRead")}
          >
            <IconEye size={14} />
          </button>
        )}
        <button
          type="button"
          className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
          onClick={handleDelete}
          aria-label={t("notificationDrawerDeleteBtn")}
        >
          <IconTrash size={14} />
        </button>
      </div>
    </div>
  );
};

const BankStatusBanner = ({banks, t}) => {
  const downBanks = banks.filter(b => !b.isActive);
  const okBanks = banks.filter(b => b.isActive);

  return (
    <div className={styles.bankBannerGroup}>
      {downBanks.length > 0 && (
        <div className={`${styles.bankBanner} ${styles.bankBannerDown}`}>
          <div className={styles.bankBannerIcon}>
            <IconAlertCircle
              size={18}
              className={styles.sirenFlash}
            />
          </div>
          <div className={styles.bankBannerContent}>
            {downBanks.map(bank => (
              <span
                key={bank.code}
                className={styles.bankBannerText}
              >
                {bank.name} {t("bankStatusDown")}
              </span>
            ))}
          </div>
        </div>
      )}
      {okBanks.length > 0 && (
        <div className={`${styles.bankBanner} ${styles.bankBannerOk}`}>
          <div className={styles.bankBannerIcon}>
            <IconHeart
              size={18}
              className={styles.heartPulse}
            />
          </div>
          <div className={styles.bankBannerContent}>
            <span className={styles.bankBannerText}>{t("bankStatusOk")}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const NotificationMenu = () => {
  const {feed, refetch} = useNotificationsFeed();
  const {markAsRead} = usePutMarkAsReadNotification();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [mockPush, setMockPush] = useState(MOCK_PUSH);
  const [newItemId, setNewItemId] = useState(null);
  const navigate = useNavigate();
  const {t} = useTranslation();

  const realNews = useMemo(() => feed.filter(item => item.type === "news").slice(0, 4), [feed]);

  const combinedFeed = useMemo(
    () => [...mockPush, ...realNews].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [mockPush, realNews]
  );

  const simulateNewEntry = () => {
    const entry = {...NEW_NOTIFICATION, id: `push-new-${Date.now()}`, createdAt: new Date().toISOString()};
    setNewItemId(entry.id);
    setMockPush(prev => [entry, ...prev]);
    setTimeout(() => setNewItemId(null), 600);
  };

  useScrollLock(open);

  const filteredFeed = useMemo(() => {
    if (activeTab === "all") return combinedFeed;
    if (activeTab === "news") return combinedFeed.filter(item => item.type === "news");
    if (activeTab === "breaking") return combinedFeed.filter(item => item.type === "push" && item.subtype === "promo");
    if (activeTab === "movements") return combinedFeed.filter(item => item.type === "push" && item.subtype === "money");
    return combinedFeed;
  }, [combinedFeed, activeTab]);

  const unreadCount = useMemo(() => combinedFeed.filter(item => !item.isRead).length, [combinedFeed]);

  const handleNavigate = useCallback(
    item => {
      if (!item.isRead && item.type === "push" && item.originalId) {
        markAsRead(item.originalId);
      }
      if (item.link) {
        navigate(item.link);
      }
      setOpen(false);
    },
    [navigate, markAsRead]
  );

  const handleDelete = useCallback(id => {
    setMockPush(prev => prev.filter(item => item.id !== id && item.originalId !== id));
  }, []);

  const handleMarkRead = useCallback(id => {
    setMockPush(prev => prev.map(item => (item.id === id || item.originalId === id ? {...item, isRead: true} : item)));
  }, []);

  const [dismissedNews, setDismissedNews] = useState([]);

  const handleDismissNews = useCallback(id => {
    setDismissedNews(prev => [...prev, id]);
  }, []);

  const filteredFeedWithDismissed = useMemo(
    () => filteredFeed.filter(item => !dismissedNews.includes(item.id)),
    [filteredFeed, dismissedNews]
  );

  const handleClose = () => {
    setOpen(false);
    refetch();
  };

  return (
    <>
      {/* Bell Trigger */}
      <div
        className={styles.bellWrapper}
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === "Enter" && setOpen(true)}
        aria-label={t("notificationDrawerTitle")}
      >
        <IconBell className={styles.bellIcon} />
        {unreadCount > 0 && <span className={styles.bellBadge}>{unreadCount > 99 ? "99+" : unreadCount}</span>}
      </div>

      {/* Overlay */}
      <div
        className={`${styles.overlay} ${open ? styles.overlayVisible : ""}`}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <aside
        className={`${styles.sidebar} ${open ? styles.sidebarOpen : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={t("notificationDrawerTitle")}
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            {t("notificationDrawerTitle")}
            {unreadCount > 0 && <span className={styles.headerBadge}>{unreadCount}</span>}
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={handleClose}
            aria-label={t("notificationClose")}
          >
            <IconX size={20} />
          </button>
        </div>

        {/* Bank Status */}
        <BankStatusBanner
          banks={MOCK_BANK_STATUS}
          t={t}
        />

        {/* Tabs */}
        <div
          className={styles.tabs}
          role="tablist"
        >
          {TABS.map(({key, icon: Icon}) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={activeTab === key}
              className={`${styles.tab} ${activeTab === key ? styles.tabActive : ""}`}
              onClick={() => setActiveTab(key)}
            >
              <Icon size={14} />
              {t(`notificationTab_${key}`)}
            </button>
          ))}
        </div>

        {/* Simulate new entry button */}
        <div className={styles.simulateBar}>
          <button
            type="button"
            className={styles.simulateBtn}
            onClick={simulateNewEntry}
          >
            Simular nueva notificación
          </button>
        </div>

        {/* Aqui va un espacio para publicidad con un alto fijo de 100px*/}
        <div className={styles.adSlot}>
          <div className={styles.adContainer}>
            <img
              src={imgAygLogo}
              alt="Publicidad"
              className={styles.adImage}
            />
          </div>
        </div>

        {/* Feed */}
        <div className={styles.feedContainer}>
          {filteredFeedWithDismissed.length > 0 ? (
            filteredFeedWithDismissed.map(item =>
              item.type === "news" ? (
                <div
                  key={item.id}
                  className={newItemId === item.id ? styles.slideIn : undefined}
                >
                  <NewsCard
                    item={item}
                    onNavigate={handleNavigate}
                    onDismiss={handleDismissNews}
                    t={t}
                  />
                </div>
              ) : (
                <div
                  key={item.id}
                  className={newItemId === item.id ? styles.slideIn : undefined}
                >
                  <PushCard
                    item={item}
                    onNavigate={handleNavigate}
                    onDelete={handleDelete}
                    onMarkRead={handleMarkRead}
                    t={t}
                  />
                </div>
              )
            )
          ) : (
            <div className={styles.emptyState}>
              <IconBellOff
                size={48}
                className={styles.emptyIcon}
              />
              <p className={styles.emptyTitle}>{t("notificationDrawerNotifications")}</p>
              <p className={styles.emptySubtext}>{t("notificationEmptySubtext")}</p>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default NotificationMenu;
